import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Clock, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TestTaking = () => {
  const { testId } = useParams();
  const navigate = useNavigate();
  const [test, setTest] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [timeElapsed, setTimeElapsed] = useState(0);
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchTest();
  }, [testId]);

  useEffect(() => {
    if (test) {
      const timer = setInterval(() => {
        setTimeElapsed((prev) => prev + 1);
      }, 60000);
      return () => clearInterval(timer);
    }
  }, [test]);

  const fetchTest = async () => {
    try {
      const response = await axios.get(`${API}/tests/${testId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTest(response.data);
      setAnswers(new Array(response.data.questions.length).fill(null));
    } catch (error) {
      toast.error('Failed to load test');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (questionIndex, answer) => {
    const newAnswers = [...answers];
    newAnswers[questionIndex] = { question_index: questionIndex, answer };
    setAnswers(newAnswers);
  };

  const handleSubmit = async () => {
    const childId = window.location.pathname.includes('/child/dashboard/') 
      ? window.location.pathname.split('/child/dashboard/')[1] 
      : null;

    if (!childId) {
      toast.error('Unable to determine child ID');
      return;
    }

    try {
      const response = await axios.post(
        `${API}/test-results`,
        {
          test_id: testId,
          child_id: childId,
          answers,
          time_taken_minutes: timeElapsed,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Test completed! Score: ${response.data.score}/${response.data.total_questions}`);
      navigate(-1);
    } catch (error) {
      toast.error('Failed to submit test');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-lg">Loading test...</p>
      </div>
    );
  }

  const question = test.questions[currentQuestion];

  return (
    <div className="min-h-screen p-4 md:p-8 lg:p-12">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="card-base p-6 mb-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="font-heading text-2xl font-bold">{test.title}</h1>
              <p className="text-sm text-muted-foreground">
                Question {currentQuestion + 1} of {test.questions.length}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-muted-foreground" />
              <span className="font-semibold">{timeElapsed} min</span>
            </div>
          </div>
          <div className="w-full bg-muted rounded-full h-2 mt-4">
            <div
              className="bg-primary h-2 rounded-full transition-all"
              style={{ width: `${((currentQuestion + 1) / test.questions.length) * 100}%` }}
            />
          </div>
        </div>

        {/* Question */}
        <div className="card-base p-8 mb-6">
          <h2 className="text-xl font-bold mb-6">{question.question}</h2>
          <RadioGroup
            value={answers[currentQuestion]?.answer}
            onValueChange={(value) => handleAnswer(currentQuestion, value)}
          >
            {question.options.map((option, idx) => (
              <div key={idx} className="flex items-center space-x-2 p-4 rounded-lg hover:bg-muted cursor-pointer">
                <RadioGroupItem value={option} id={`option-${idx}`} />
                <Label htmlFor={`option-${idx}`} className="flex-1 cursor-pointer">
                  {option}
                </Label>
              </div>
            ))}
          </RadioGroup>
        </div>

        {/* Navigation */}
        <div className="flex justify-between">
          <Button
            data-testid="prev-question-btn"
            variant="outline"
            onClick={() => setCurrentQuestion(currentQuestion - 1)}
            disabled={currentQuestion === 0}
            className="rounded-full"
          >
            Previous
          </Button>
          
          {currentQuestion === test.questions.length - 1 ? (
            <Button
              data-testid="submit-test-btn"
              onClick={handleSubmit}
              className="rounded-full"
            >
              <CheckCircle className="mr-2 h-4 w-4" />
              Submit Test
            </Button>
          ) : (
            <Button
              data-testid="next-question-btn"
              onClick={() => setCurrentQuestion(currentQuestion + 1)}
              className="rounded-full"
            >
              Next
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TestTaking;